import { useCallback, useEffect, useMemo, useState } from "react";
import { message } from "antd";
import { ReviContentApi } from "../../../api/revi/ReviContentApi";
import { useAuth } from "../../../hooks";

function normalizeTherapyCats(json) {
  return (
    json?.therapies ||
    json?.categories ||
    json?.therapyCategories ||
    json?.data ||
    json ||
    []
  );
}

function normalizeScenes(json) {
  const scenes = json?.scenes || json?.data || json || [];
  const pagination = json?.pagination || {};
  const total =
    pagination?.totalDocs ??
    pagination?.total ??
    json?.totalDocs ??
    json?.total ??
    scenes.length;
  return { scenes, total };
}

function normalizeTherapyIds(raw) {
  return (raw || [])
    .map((x) => {
      if (!x) return null;
      if (typeof x === "string") return x;
      if (typeof x === "object" && x._id) return String(x._id);
      if (typeof x === "object" && x.id) return String(x.id);
      return null;
    })
    .filter(Boolean);
}

export function useReviSceneController() {
  const { accessToken } = useAuth();
  const api = useMemo(() => new ReviContentApi(), []);

  const [therapyCats, setTherapyCats] = useState([]);
  const [loadingCats, setLoadingCats] = useState(false);

  const [scenesRaw, setScenesRaw] = useState([]);
  const [total, setTotal] = useState(0);
  const [loadingScenes, setLoadingScenes] = useState(false);

  const [creating, setCreating] = useState(false);
  const [error, setError] = useState(null);

  const [filters, setFilters] = useState({
    page: 1,
    limit: 12,
    search: "",
    therapyId: null,
    difficulty: null,
    premium: null,
    active: null,
  });

  const scenes = useMemo(() => scenesRaw, [scenesRaw]);

  const therapyMap = useMemo(() => {
    const m = new Map();
    (therapyCats || []).forEach((t) => m.set(String(t._id), t));
    return m;
  }, [therapyCats]);

  // --------------------------------------------------------------------------
  // READ
  // --------------------------------------------------------------------------

  const fetchTherapyCats = useCallback(async () => {
    if (!accessToken) return;
    setLoadingCats(true);
    try {
      const json = await api.listTherapyCategories(accessToken, {
        active: true,
      });
      setTherapyCats(normalizeTherapyCats(json));
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error("Error cargando categorías:", e);
      message.error("No se pudieron cargar las terapias.");
    } finally {
      setLoadingCats(false);
    }
  }, [api, accessToken]);

  const fetchScenes = useCallback(async () => {
    if (!accessToken) return;
    setLoadingScenes(true);
    setError(null);
    try {
      const json = await api.listScenes(accessToken, filters);
      const norm = normalizeScenes(json);
      setScenesRaw(norm.scenes);
      setTotal(norm.total);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error("Error cargando escenas:", e);
      setError(e);
      message.error(e?.message || "Error cargando escenas.");
    } finally {
      setLoadingScenes(false);
    }
  }, [api, accessToken, filters]);

  useEffect(() => {
    fetchTherapyCats();
  }, [fetchTherapyCats]);

  useEffect(() => {
    fetchScenes();
  }, [fetchScenes]);

  // --------------------------------------------------------------------------
  // ASSETS
  // --------------------------------------------------------------------------

  const uploadBanner = useCallback(
    async ({ idScene, title, file }) => {
      if (!accessToken) throw new Error("Sin sesión activa (token)");

      const uploader =
        api.uploadBanner?.bind(api) || api.uploadSceneBanner?.bind(api);

      if (!uploader) {
        throw new Error(
          "ReviContentApi: falta método uploadBanner/uploadSceneBanner",
        );
      }

      return uploader(accessToken, {
        idScene,
        title,
        description: "Banner REVI Uploaded via Wizard",
        isPublic: true,
        file,
      });
    },
    [api, accessToken],
  );

  const deleteBanner = useCallback(
    async (fileId) => {
      if (!accessToken || !fileId) return;
      try {
        await api.deleteBanner(accessToken, fileId);
      } catch (e) {
        // eslint-disable-next-line no-console
        console.warn(
          "No se pudo borrar el banner (no crítico):",
          e?.message || e,
        );
      }
    },
    [api, accessToken],
  );

  // --------------------------------------------------------------------------
  // DELETE
  // --------------------------------------------------------------------------

  const deleteScene = useCallback(
    async (sceneId) => {
      if (!accessToken) return;
      try {
        await api.deleteReviScene(accessToken, sceneId);
        message.success("Escena eliminada correctamente");
        // ⚠️ refresh no debe romper UX
        try {
          await fetchScenes();
        } catch (e) {
          // eslint-disable-next-line no-console
          console.warn(
            "Refresh falló tras borrar (no crítico):",
            e?.message || e,
          );
        }
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error("Error borrando escena:", e);
        message.error(`Error al borrar: ${e?.message || "Desconocido"}`);
        throw e;
      }
    },
    [api, accessToken, fetchScenes],
  );

  // --------------------------------------------------------------------------
  // CREATE (Wizard)
  // --------------------------------------------------------------------------

  const createScene = useCallback(
    async ({ identity, clinical, assets }) => {
      if (!accessToken) {
        message.error("Sesión expirada");
        return;
      }

      setCreating(true);
      setError(null);

      try {
        let bannerId = null;

        if (assets?.bannerFile) {
          const uploadRes = await uploadBanner({
            idScene: identity.idScene,
            title: identity.title,
            file: assets.bannerFile,
          });

          bannerId = uploadRes?.id || uploadRes?._id || uploadRes?.file?._id;
          if (!bannerId)
            throw new Error("El servidor no devolvió ID del banner.");
        }

        // ✅ tags terapias para module_07 (si backend los usa)
        const therapyTags = (clinical.therapyIds || [])
          .map((id) => therapyMap.get(String(id))?.title)
          .filter(Boolean);

        // ✅ payload robusto: flat + nested (por compatibilidad)
        const payloadMinimal = {
          idScene: identity.idScene,
          ModOneTitle: identity.title,
          ModOneUrlsala: identity.hubsUrl,
          ModOneDescription: clinical.clinicalDescription || "",
          bannertop: bannerId || undefined,
          ModOneCategory: ["REVI"],
          ModOneSubcategory: ["SALAS"],
          ModOneTags: ["revi"],
          ModOneActive: true,

          // Idioma del CONTENIDO de la sala. Viaja en la raíz del payload y no
          // dentro de module_07 a propósito: el auto-tagger reescribe ese
          // bloque y lo borraría en el siguiente análisis.
          //
          // Se envían solo si el formulario dice algo. Un alta que no menciona
          // el idioma deja la sala "sin clasificar", que es un estado válido y
          // donde están hoy todas.
          ...(identity.sceneLanguageNeutral
            ? { sceneLanguageNeutral: true }
            : {}),
          ...(!identity.sceneLanguageNeutral && identity.sceneLanguages?.length
            ? { sceneLanguages: identity.sceneLanguages }
            : {}),

          module_00: { idScene: identity.idScene },
          module_01: {
            ModOneTitle: identity.title,
            ModOneUrlsala: identity.hubsUrl,
            ModOneDescription: clinical.clinicalDescription || "",
            bannertop: bannerId || undefined,
            ModOneCategory: ["REVI"],
            ModOneSubcategory: ["SALAS"],
            ModOneTags: ["revi"],
            ModOneActive: true,
          },
        };

        const createdRes = await api.createReviSceneMinimal(
          accessToken,
          payloadMinimal,
        );

        const sceneId =
          createdRes?.id ||
          createdRes?.virtualScene?._id ||
          createdRes?.scene?._id ||
          createdRes?._id;

        if (!sceneId)
          throw new Error("No se pudo obtener el ID de la escena creada.");

        const module07Payload = {
          ModSevenClinicalDescription: clinical.clinicalDescription || "",
          ModSevenInterventionGuide: clinical.interventionGuide || "",
          ModSevenContraindications: clinical.contraindications || "",
          ModSevenDifficultyLevel: clinical.difficulty || "Todos",
          ModSevenTherapyIds: clinical.therapyIds || [],
          ModSevenTherapyTags: therapyTags, // ✅
          ModSevenClinicalFocus: clinical.clinicalFocus || [],
          ModSevenActive: clinical.active ?? true,
          ReviScenePremium: Boolean(clinical.premium), // ✅ nueva variable
        };

        await api.createModule07(accessToken, sceneId, module07Payload);

        message.success("Sala creada correctamente");

        // ⚠️ refresh no debe impedir cerrar wizard
        try {
          await fetchScenes();
        } catch (e) {
          // eslint-disable-next-line no-console
          console.warn(
            "Refresh falló tras crear (no crítico):",
            e?.message || e,
          );
        }
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error("Error creando sala:", e);
        setError(e);
        message.error(e?.message || "Error creando la sala.");
        throw e;
      } finally {
        setCreating(false);
      }
    },
    [accessToken, api, fetchScenes, therapyMap, uploadBanner],
  );

  // --------------------------------------------------------------------------
  // ✅ EDIT FLOW (load + update)
  // --------------------------------------------------------------------------

  const getEditData = useCallback(
    async (sceneId) => {
      if (!accessToken) throw new Error("Sin sesión activa (token)");

      const base = await api.getReviSceneById(accessToken, sceneId);
      const scene = base?.scene || base?.virtualScene || base?.data || base;

      const isReviBase =
        String(scene?.module_00?.departament || "").toLowerCase() === "revi";

      let module07 = null;
      try {
        const m07 = await api.getModule07(accessToken, sceneId);
        module07 = m07?.moduleData || m07?.module_07 || null;
      } catch (e) {
        module07 = scene?.module_07 || null;
      }

      const idScene = scene?.module_00?.idScene || "";
      const title = scene?.module_01?.ModOneTitle || "";
      const hubsUrl = scene?.module_01?.ModOneUrlsala || "";

      // ✅ FIX:
      // - Clínico debe venir de module_07.
      // - Solo si es base REVI y no existe module_07, permitimos fallback a module_01 para no romper escenas antiguas.
      const clinicalDescription =
        module07?.ModSevenClinicalDescription ||
        (isReviBase ? scene?.module_01?.ModOneDescription : "") ||
        "";

      const therapyIds = normalizeTherapyIds(module07?.ModSevenTherapyIds);
      const difficulty = module07?.ModSevenDifficultyLevel || "Todos";
      const premium = Boolean(module07?.ReviScenePremium);
      const active = module07?.ModSevenActive ?? true;

      const interventionGuide = module07?.ModSevenInterventionGuide || "";
      const contraindications = module07?.ModSevenContraindications || "";
      const clinicalFocus = module07?.ModSevenClinicalFocus || [];

      const bannerObj = scene?.module_01?.bannertop;
      const existingBannerId =
        typeof bannerObj === "string"
          ? bannerObj
          : bannerObj?._id
            ? String(bannerObj._id)
            : null;

      const existingBannerUrl =
        typeof bannerObj === "object" && bannerObj?.url ? bannerObj.url : null;

      const existingBannerName =
        typeof bannerObj === "object" && bannerObj?.title
          ? bannerObj.title
          : "banner";

      const bannerFileList = existingBannerUrl
        ? [
            {
              uid: `existing-${existingBannerId || "banner"}`,
              name: existingBannerName,
              status: "done",
              url: existingBannerUrl,
            },
          ]
        : [];

      return {
        sceneId,
        existingBannerId,
        isReviBase, // ✅ extra (no rompe)
        bannerFileList,
        initialValues: {
          idScene,
          title,
          hubsUrl,
          bannerId:
            existingBannerId ||
            (bannerFileList.length ? "existing" : undefined),
          therapyIds,
          difficulty,
          premium,
          active,
          clinicalDescription,
          clinicalFocus,
          interventionGuide,
          contraindications,
          // Sin precargar el idioma, editar una sala francesa la ensenaria
          // como "sin clasificar" y guardar cualquier otro cambio la dejaria
          // asi de verdad. Se lee de la raiz del documento, donde vive.
          sceneLanguages: scene?.sceneLanguages || [],
          sceneLanguageNeutral: scene?.sceneLanguageNeutral === true,
        },
      };
    },
    [accessToken, api],
  );

  const updateScene = useCallback(
    async (sceneId, { identity, clinical, assets, meta }) => {
      if (!accessToken) {
        message.error("Sesión expirada");
        return;
      }

      setCreating(true);
      setError(null);

      try {
        const existingBannerId = meta?.existingBannerId || null;
        const isReviBase =
          meta?.isReviBase === undefined ? undefined : Boolean(meta.isReviBase);

        let newBannerId = null;

        // Sala compartida con Educa360: el servidor no toca su banner (es
        // contenido de la otra vertical) y la limpieza de mas abajo borraria
        // el banner de Educa360. No se sube nada.
        if (assets?.bannerFile && isReviBase !== false) {
          const uploadRes = await uploadBanner({
            idScene: identity.idScene,
            title: identity.title,
            file: assets.bannerFile,
          });

          newBannerId = uploadRes?.id || uploadRes?._id || uploadRes?.file?._id;
          if (!newBannerId)
            throw new Error("El servidor no devolvió ID del banner.");
        }

        const therapyTags = (clinical.therapyIds || [])
          .map((id) => therapyMap.get(String(id))?.title)
          .filter(Boolean);

        // ✅ FIX:
        // - NO volver a enviar clinicalDescription a module_01 (eso fue lo que contaminó EDUCA)
        const patchBase = {
          ModOneTitle: identity.title,
          ModOneUrlsala: identity.hubsUrl,
          ...(newBannerId ? { bannertop: newBannerId } : {}),
          // El idioma tambien se puede corregir editando: es justo lo que hay
          // que hacer con las salas que se subieron antes de que el alta lo
          // guardara. Misma guardia que en el alta -solo viaja si dice algo-
          // para que un PATCH que no toca el idioma no desclasifique la sala.
          ...(identity.sceneLanguageNeutral
            ? { sceneLanguageNeutral: true }
            : {}),
          ...(!identity.sceneLanguageNeutral && identity.sceneLanguages?.length
            ? { sceneLanguages: identity.sceneLanguages }
            : {}),
          module_01: {
            ModOneTitle: identity.title,
            ModOneUrlsala: identity.hubsUrl,
            ...(newBannerId ? { bannertop: newBannerId } : {}),
          },
        };

        // El PATCH base se llama SIEMPRE, también para las salas compartidas
        // con Educa360: desde el 2026-09-04 el servidor aplica en ellas el
        // título y la URL y devuelve qué ha aplicado y qué ha ignorado.
        //
        // Antes devolvía 403, este código se lo tragaba y el admin veía
        // "Cambios guardados correctamente" con la URL sin cambiar. Si un
        // servidor antiguo aún devuelve 403, se dice en vez de esconderlo.
        let respuestaBase = null;
        let baseRechazada = false;
        let motivoBase = "";
        try {
          respuestaBase = await api.updateReviSceneMinimal(
            accessToken,
            sceneId,
            patchBase,
          );
        } catch (e) {
          // 403 (servidor antiguo) o 400 (el servidor valida titulo/URL algo
          // mas estricto que el formulario): la base no se aplica, pero el
          // bloque clinico se guarda igual y se avisa con el motivo.
          if (e?.status === 403 || e?.status === 400) {
            baseRechazada = true;
            motivoBase = e?.message || "";
          } else {
            throw e;
          }
        }

        const patch07 = {
          ModSevenClinicalDescription: clinical.clinicalDescription || "",
          ModSevenInterventionGuide: clinical.interventionGuide || "",
          ModSevenContraindications: clinical.contraindications || "",
          ModSevenDifficultyLevel: clinical.difficulty || "Todos",
          ModSevenTherapyIds: clinical.therapyIds || [],
          ModSevenTherapyTags: therapyTags,
          ModSevenClinicalFocus: clinical.clinicalFocus || [],
          ModSevenActive: clinical.active ?? true,
          ReviScenePremium: Boolean(clinical.premium),
        };

        await api.updateModule07(accessToken, sceneId, patch07);

        // El banner anterior solo se borra si el servidor ha aplicado el
        // nuevo. Un servidor antiguo no dice qué aplicó: si el PATCH fue bien,
        // lo aplicó. Si el nuevo no se ha usado, es él el que sobra.
        const bannerAplicado = Array.isArray(respuestaBase?.aplicados)
          ? respuestaBase.aplicados.includes("bannertop")
          : Boolean(respuestaBase);

        if (newBannerId && !bannerAplicado) {
          await deleteBanner(newBannerId);
        } else if (
          newBannerId &&
          existingBannerId &&
          String(existingBannerId) !== String(newBannerId)
        ) {
          await deleteBanner(existingBannerId);
        }

        if (baseRechazada) {
          message.warning(
            `Datos clínicos guardados, pero el título y la URL NO se han aplicado: el servidor ha rechazado editar la base de esta sala${
              motivoBase ? ` (${motivoBase})` : ""
            }.`,
            8,
          );
        } else if (respuestaBase?.base === "educa360") {
          message.success(
            "Cambios guardados. El título y la URL se han aplicado también en el catálogo Educa360: es la misma sala.",
            6,
          );
        } else {
          message.success("Cambios guardados correctamente");
        }

        try {
          await fetchScenes();
        } catch (e) {
          // eslint-disable-next-line no-console
          console.warn(
            "Refresh falló tras editar (no crítico):",
            e?.message || e,
          );
        }
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error("Error actualizando sala:", e);
        setError(e);
        message.error(e?.message || "Error guardando cambios.");
        throw e;
      } finally {
        setCreating(false);
      }
    },
    [accessToken, api, deleteBanner, fetchScenes, therapyMap, uploadBanner],
  );

  return {
    state: {
      therapyCats,
      scenes,
      total,
      loadingCats,
      loadingScenes,
      creating,
      error,
      filters,
    },
    actions: {
      reload: fetchScenes,
      setFilterPatch: (patch) =>
        setFilters((prev) => ({ ...prev, ...patch, page: 1 })),
      setPage: (page, limit) =>
        setFilters((prev) => ({ ...prev, page, limit })),
      deleteBanner,
      createScene,
      deleteScene,
      getEditData,
      updateScene,
    },
  };
}
